import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { listFolders, createFolder, deleteFolder } from "@/lib/folders";
import type { Folder } from "@/types";

interface Props {
  selected: string | null | "unfiled";
  onSelect: (id: string | null | "unfiled") => void;
}

export function FolderChips({ selected, onSelect }: Props) {
  const { t } = useTranslation();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listFolders().then(setFolders).catch(() => {});
  }, []);

  const submit = async () => {
    const name = draft.trim();
    if (!name) {
      setCreating(false);
      return;
    }
    try {
      const folder = await createFolder(name);
      setFolders((f) => [...f, folder]);
      setDraft("");
      setCreating(false);
      onSelect(folder.id);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const remove = async (folder: Folder) => {
    if (!window.confirm(t("folders.deleteMsg", { name: folder.name }))) return;
    try {
      await deleteFolder(folder.id);
      setFolders((f) => f.filter((x) => x.id !== folder.id));
      if (selected === folder.id) onSelect(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      <Chip
        label={t("folders.all")}
        active={selected === null}
        onClick={() => onSelect(null)}
      />
      <Chip
        label={t("folders.unfiled")}
        active={selected === "unfiled"}
        onClick={() => onSelect("unfiled")}
      />
      {folders.map((f) => (
        <div key={f.id} className="flex items-center gap-1">
          <Chip
            label={f.name}
            active={selected === f.id}
            onClick={() => onSelect(f.id)}
          />
          <button
            onClick={() => remove(f)}
            className="text-redi-muted hover:text-redi-error text-xs px-1"
            title={t("common.delete")}
          >
            ×
          </button>
        </div>
      ))}

      {creating ? (
        <div className="flex items-center gap-1 bg-redi-surface rounded-full px-2 py-1">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") {
                setCreating(false);
                setDraft("");
              }
            }}
            placeholder={t("folders.namePlaceholder")}
            maxLength={40}
            className="bg-transparent text-redi-text text-xs px-2 py-0.5 outline-none w-32"
          />
          <button
            onClick={submit}
            className="text-redi-primary text-xs px-1"
          >
            ✓
          </button>
        </div>
      ) : (
        <button
          onClick={() => setCreating(true)}
          className="bg-redi-surface text-redi-muted hover:text-redi-text text-xs rounded-full px-3 py-1.5"
        >
          + {t("folders.new")}
        </button>
      )}

      {error && (
        <p className="w-full text-redi-error text-xs">{error}</p>
      )}
    </div>
  );
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
        active
          ? "bg-redi-primary text-white font-semibold"
          : "bg-redi-surface text-redi-muted hover:text-redi-text"
      }`}
    >
      {label}
    </button>
  );
}
